// --- START PATCH: Cleanup (remove experimental focus restoration suppression & navigation origin tracking) ---
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/lib/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/lib/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { toast } from "sonner";
import clsx from "clsx";
import React from "react";
import { Button } from "@/lib/components/ui/button";
import { useTranslation } from "react-i18next";

type CompanyStatus = "processing" | "approved" | "rejected" | "revoked" | "withdrawn";

interface UserShape {
  username: string;
  email: string;
  is_admin: boolean;
  company_review_status: CompanyStatus | null;
}

const badgeBase =
  "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium leading-none";

function CompanyStatusBadge({
  status,
}: {
  status: NonNullable<UserShape["company_review_status"]>;
}) {
  const { t } = useTranslation('account');
  const map: Record<
    NonNullable<UserShape["company_review_status"]>,
    { className: string; labelKey: string }
  > = {
    processing: {
      className: "bg-muted text-foreground border border-border",
      labelKey: "status.pending",
    },
    approved: {
      className: "bg-green-600 text-white",
      labelKey: "status.approved",
    },
    rejected: {
      className: "bg-red-600 text-white",
      labelKey: "status.rejected",
    },
    revoked: {
      className: "bg-orange-600 text-white",
      labelKey: "status.revoked",
    },
    withdrawn: {
      className: "bg-gray-500 text-white",
      labelKey: "status.withdrawn",
    },
  };
  const cfg = map[status];
  return (
    <span
      aria-hidden="true"
      className={clsx(badgeBase, "ml-2", cfg.className)}
      title={t(cfg.labelKey)}
    >
      {t(cfg.labelKey)}
    </span>
  );
}

const AccountMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('account');
  const u = user as UserShape | undefined;

  const handleLogout = async () => {
    await logout();
    toast.success(t('logout'));
  };

  // Anonymous visitor (no experimental focus suppression logic)
  if (!u) {
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="rounded-full">
            {t('account')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="w-56 z-50">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {t('account')}
          </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/login")}>{t('login')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/signup")}>{t('createAccount')}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const isAdmin = u.is_admin;
  const companyStatus = u.company_review_status;

  const companyItems: React.ReactNode[] = [];
  if (!isAdmin) {
    if (companyStatus === null) {
      companyItems.push(
        <DropdownMenuItem key="company-apply" onClick={() => navigate("/account/company")}>
          {t('applyAsCompany')}
        </DropdownMenuItem>
      );
    } else if (companyStatus === "processing") {
      companyItems.push(
        <DropdownMenuItem key="company-pending" onClick={() => navigate("/account/company")}>
          {t('companyStatus')}: {t('status.pending')} <CompanyStatusBadge status="processing" />
        </DropdownMenuItem>
      );
    } else if (companyStatus === "rejected") {
      companyItems.push(
        <DropdownMenuItem key="company-reapply" onClick={() => navigate("/account/company")}>
          {t('reapplyAsCompany')} <CompanyStatusBadge status="rejected" />
        </DropdownMenuItem>
      );
    } else if (companyStatus === "revoked") {
      companyItems.push(
        <DropdownMenuItem key="company-revoked" onClick={() => navigate("/account/company")}>
          {t('companyStatus')}: {t('status.revoked')} <CompanyStatusBadge status="revoked" />
        </DropdownMenuItem>
      );
    } else if (companyStatus === "withdrawn") {
      companyItems.push(
        <DropdownMenuItem key="company-withdrawn" onClick={() => navigate("/account/company")}>
          {t('reapplyAsCompany')} <CompanyStatusBadge status="withdrawn" />
        </DropdownMenuItem>
      );
    } else if (companyStatus === "approved") {
      companyItems.push(
        <DropdownMenuItem
          key="company-approved"
          data-disabled
          aria-disabled="true"
          className="opacity-90 cursor-default select-none"
        >
          {t('companyApproved')} <CompanyStatusBadge status="approved" />
        </DropdownMenuItem>
      );
    }
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-label="Account menu"
          className="rounded-full focus:outline-none"
        >
          <Avatar className="w-8 h-8">
            <AvatarFallback>
              {u.email?.charAt(0)?.toUpperCase() ?? "A"}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={8} className="w-56 z-50">
        <div
          className="px-2 py-2 text-xs leading-snug text-muted-foreground max-w-full break-words"
          aria-label={`${t('signedInAs')} ${u.email}`}
          title={u.email}
        >
          {t('signedInAs')}{" "}
          <span className="font-medium text-foreground">{u.email}</span>
        </div>

        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {t('account')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/account/profile")}>{t('profile')}</DropdownMenuItem>
        {!isAdmin && (
          <DropdownMenuItem onClick={() => navigate("/account/orders")}>{t('myOrders')}</DropdownMenuItem>
        )}

        {!isAdmin && companyItems.length > 0 && (
          <>
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2">
              {t('company')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {companyItems}
          </>
        )}

        {isAdmin && (
          <>
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2">
              {t('admin')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/admin/inventory")}>
              {t('inventory')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/admin/clients")}>
              {t('clientManager')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/admin/orders")}>
              {t('orders')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/admin/store-settings")}>
              {t('storeSettings')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/admin/shipping-methods")}>
              {t('shippingMethods')}
            </DropdownMenuItem>

            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2">
              {t('catalog')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/admin/catalog/products")}>
              {t('catalogProducts')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/admin/catalog/filaments")}>
              {t('catalogFilaments')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/admin/catalog/categories")}>
              {t('catalogCategories')}
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2">
          {t('settings')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/account/settings")}>{t('settingsPage')}</DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="text-red-600 focus:text-red-700 focus:bg-red-50 dark:focus:bg-red-950/40"
        >
          {t('logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccountMenu;
// --- END PATCH: Cleanup (remove experimental focus restoration suppression & navigation origin tracking) ---